import React from "react";
import { bindActionCreators, Dispatch } from "redux";
import { connect } from "react-redux";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { LeadTagEdit } from "./lead-tag-edit";
import { AgentTag } from "component/shared/agent-tag";
import { Lead } from "model/lead";
import { LeadStatus } from "type/lead-status";
import { updateLead } from "redux/slice/leads";
import { urls } from "routes/urls";
import { styles } from "./style";
import { User } from "model/user";
import * as env from "shared/env";
import { getActiveLeadPhonesForLead, getActiveLeads } from "redux/selector";
import { RootState } from "redux/store";
import { getActiveLeadAnniversariesForLead } from "redux/selector";
import { formatPhoneNumber } from "shared/phone-number-formatter";
import { getTypeTagsForLead } from "redux/selector";
import {
	monthDayFormatter,
	numberedDateFormatter,
} from "shared/date-formatter";
import { Ranking } from "component/shared/ranking";
import { EditLeadDialog } from "component/shared/edit-lead-dialog";
import { calculateLeadRankSize } from "shared/lead-rank-size";
import { LeadReadiness } from "type/lead-readiness";
import {
	formatLeadTimeline,
	formatLeadPrice,
	formatLeadPreApproval,
	hasLeadTimeline,
} from "shared/lead-readiness-formatter";
import { YesNoType } from "type/boolean-choice-type";
import { AppCard } from "component/shared/app-card";

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
	return {
		leadPhones: getActiveLeadPhonesForLead(state, ownProps.lead.id),
		leads: getActiveLeads(state),
		leadAnniversaries: getActiveLeadAnniversariesForLead(state, ownProps.lead.id),
		typeTags: getTypeTagsForLead(state, ownProps.lead.id),
	};
};

const mapDispatchToProps = (dispatch: Dispatch) =>
	bindActionCreators(
		{
			updateLead,
		},
		dispatch
	);

interface Props
	extends ReturnType<typeof mapStateToProps>,
		ReturnType<typeof mapDispatchToProps>,
		WithStyles<typeof styles> {
	user: User;
	lead: Lead;
}

interface State {
	editLeadDialogIsOpen: boolean;
}

interface OwnProps {
	user: User;
	lead: Lead;
}

class Component extends React.Component<Props, State> {
	public constructor(props: Props) {
		super(props);
		this.state = {
			editLeadDialogIsOpen: false,
		};
	}

	private mailto(email: string) {
		const { lead } = this.props;
		let result = "mailto:";
		if (lead.name) {
			result += lead.name + "<";
		}
		result += email;
		if (lead.name) {
			result += ">";
		}
		return result;
	}

	private tel(phoneNumber: string) {
		return `tel:${formatPhoneNumber(phoneNumber)}`;
	}

	private address() {
		const { lead } = this.props;
		const address = `${lead.address}, ${lead.city}, ${lead.state} ${lead.zip}`;
		return `https://www.google.com/maps/place/${encodeURI(address)}`;
	}

	private reactivateLead = () => {
		const { lead, updateLead } = this.props;
		updateLead({
			lead: {
				...lead,
				status: LeadStatus.ACTIVE,
			},
		});
	};

	public render() {
		const {
			classes = {},
			user,
			lead,
			leads,
			leadPhones,
			leadAnniversaries,
			typeTags,
		} = this.props;
		const { editLeadDialogIsOpen } = this.state;
		const leadRankData = calculateLeadRankSize([...leads]);

		const editDialog = (
			<EditLeadDialog
				open={editLeadDialogIsOpen}
				onClose={() => this.setState({ editLeadDialogIsOpen: false })}
				user={user}
				lead={lead}
				leadPhones={leadPhones}
				leadAnniversaries={leadAnniversaries}
				typeTags={typeTags}
			/>
		);

			const birthday = leadAnniversaries?.find(a => a.type.id === 1);
			return (
				<>
					{editDialog}
					<AppCard>
						<Mui.CardHeader
							title="Lead Details"
							classes={{ root: classes.root, title: classes.title }}
							action={
								<Mui.Button
									color="primary"
									onClick={() => this.setState({ editLeadDialogIsOpen: true })}
									startIcon={<Icons.Edit2 size={16} />}
								>
									Edit
								</Mui.Button>
							}
						/>
						<Mui.CardContent>
							<Mui.Grid container direction="column" spacing={2}>

								{/* General */}
								<Mui.Grid item>
									<Mui.Typography className={classes.sectionHeader}>General</Mui.Typography>
								</Mui.Grid>
								<Mui.Grid item>
									<Mui.Grid container spacing={2}>
										<Mui.Grid item xs={6}>
											<Mui.Typography className={classes.fieldLabel}>Name</Mui.Typography>
											<Mui.Typography>{lead.name}</Mui.Typography>
										</Mui.Grid>
										<Mui.Grid item xs={6}>
											<Mui.Typography className={classes.fieldLabel}>Added</Mui.Typography>
											<Mui.Typography>{numberedDateFormatter(lead.createdOn)}</Mui.Typography>
										</Mui.Grid>
										{lead.phone && (
											<Mui.Grid item xs={6}>
												<Mui.Typography className={classes.fieldLabel}>Phone</Mui.Typography>
												<Mui.Typography>
													<Mui.Link href={this.tel(lead.phone)}>{formatPhoneNumber(lead.phone)}</Mui.Link>
												</Mui.Typography>
											</Mui.Grid>
										)}
										{leadPhones && leadPhones.filter(p => !p.deleted).map(phone => (
											<Mui.Grid item xs={6} key={phone.id}>
												<Mui.Typography className={classes.fieldLabel}>
													{phone.type ? phone.type.label : "Other"}
												</Mui.Typography>
												<Mui.Typography>
													<Mui.Link href={this.tel(phone.value)}>{formatPhoneNumber(phone.value)}</Mui.Link>
												</Mui.Typography>
											</Mui.Grid>
										))}
										{lead.email && (
											<Mui.Grid item xs={6}>
												<Mui.Typography className={classes.fieldLabel}>Email</Mui.Typography>
												<Mui.Typography noWrap>
													<Mui.Link href={this.mailto(lead.email)}>{lead.email}</Mui.Link>
												</Mui.Typography>
											</Mui.Grid>
										)}
										{lead.email2 && (
											<Mui.Grid item xs={6}>
												<Mui.Typography className={classes.fieldLabel}>Email</Mui.Typography>
												<Mui.Typography noWrap>
													<Mui.Link href={this.mailto(lead.email2)}>{lead.email2}</Mui.Link>
												</Mui.Typography>
											</Mui.Grid>
										)}
										{lead.address && (
											<Mui.Grid item xs={6}>
												<Mui.Typography className={classes.fieldLabel}>Address</Mui.Typography>
												<Mui.Typography>{lead.address}</Mui.Typography>
											</Mui.Grid>
										)}
										{lead.city && (
											<Mui.Grid item xs={6}>
												<Mui.Typography className={classes.fieldLabel}>City</Mui.Typography>
												<Mui.Typography>{lead.city}</Mui.Typography>
											</Mui.Grid>
										)}
										{lead.state && (
											<Mui.Grid item xs={6}>
												<Mui.Typography className={classes.fieldLabel}>State</Mui.Typography>
												<Mui.Typography>{lead.state}</Mui.Typography>
											</Mui.Grid>
										)}
										{lead.zip && (
											<Mui.Grid item xs={6}>
												<Mui.Typography className={classes.fieldLabel}>Zip</Mui.Typography>
												<Mui.Typography>{lead.zip}</Mui.Typography>
											</Mui.Grid>
										)}
										{birthday && (
											<Mui.Grid item xs={6}>
												<Mui.Typography className={classes.fieldLabel}>Birthday</Mui.Typography>
												<Mui.Typography>{monthDayFormatter(birthday.upcomingAnniversary)}</Mui.Typography>
											</Mui.Grid>
										)}
										{lead.source && (
											<Mui.Grid item xs={6}>
												<Mui.Typography className={classes.fieldLabel}>Source</Mui.Typography>
												<Mui.Typography>{lead.source}</Mui.Typography>
											</Mui.Grid>
										)}
									</Mui.Grid>
								</Mui.Grid>
								{user.permissions.crmTags && (
									<Mui.Grid item>
										<Mui.Typography className={classes.fieldLabel}>Tags</Mui.Typography>
										<LeadTagEdit lead={lead} />
									</Mui.Grid>
								)}

								{/* Lead Readiness */}
								{user.permissions.crm && (
									<>
										<Mui.Grid item><Mui.Divider /></Mui.Grid>
										<Mui.Grid item>
											<Mui.Typography className={classes.sectionHeader}>Lead Readiness</Mui.Typography>
										</Mui.Grid>
										<Mui.Grid item>
											<Mui.Grid container spacing={2}>
												{lead.agent && (
													<Mui.Grid item xs={6}>
														<Mui.Typography className={classes.fieldLabel}>Assigned Agent</Mui.Typography>
														<AgentTag agent={lead.agent} clickable={false} />
													</Mui.Grid>
												)}
												{user.permissions.extendedLeadFields && lead.workingWithAnotherAgent !== undefined && (
													<Mui.Grid item xs={6}>
														<Mui.Typography className={classes.fieldLabel}>Other Agent?</Mui.Typography>
														<Mui.Typography>{YesNoType.getByValue(lead.workingWithAnotherAgent)?.label}</Mui.Typography>
													</Mui.Grid>
												)}
												{user.permissions.crmTags && typeTags.length > 0 && (
													<Mui.Grid item xs={6}>
														<Mui.Typography className={classes.fieldLabel}>Type</Mui.Typography>
														<Mui.Typography>{typeTags.map(tag => tag.text).join(", ")}</Mui.Typography>
													</Mui.Grid>
												)}
												{user.permissions.extendedLeadFields && hasLeadTimeline(lead.transactionTimelineEnd, lead.justBrowsing) && (
													<Mui.Grid item xs={6}>
														<Mui.Typography className={classes.fieldLabel}>Timeline</Mui.Typography>
														<Mui.Typography>
															{formatLeadTimeline(lead.transactionTimelineStart, lead.transactionTimelineEnd, lead.justBrowsing)}
														</Mui.Typography>
													</Mui.Grid>
												)}
												{user.permissions.extendedLeadFields && lead.transactionPrice !== undefined && (
													<Mui.Grid item xs={6}>
														<Mui.Typography className={classes.fieldLabel}>Price</Mui.Typography>
														<Mui.Typography>{formatLeadPrice(lead.transactionPrice)}</Mui.Typography>
													</Mui.Grid>
												)}
												{user.permissions.extendedLeadFields && lead.mortgagePreApproval !== undefined && (
													<Mui.Grid item xs={6}>
														<Mui.Typography className={classes.fieldLabel}>Pre-approval</Mui.Typography>
														<Mui.Typography>{formatLeadPreApproval(lead.mortgagePreApproval)}</Mui.Typography>
													</Mui.Grid>
												)}
												{lead.readiness && user.permissions.extendedLeadFields && (
													<Mui.Grid item xs={6}>
														<Mui.Typography className={classes.fieldLabel}>Lead Quality</Mui.Typography>
														<Mui.Chip
															label={LeadReadiness.getById(lead.readiness)?.label || "N/A"}
															color="primary"
														/>
													</Mui.Grid>
												)}
												{user.permissions.extendedLeadFields && (
													<Mui.Grid item xs={6}>
														<Mui.Typography className={classes.fieldLabel}>Referral Requested</Mui.Typography>
														<Mui.Typography>{YesNoType.getByValue(!lead.referralRequested ? false : true)?.label}</Mui.Typography>
													</Mui.Grid>
												)}
											</Mui.Grid>
										</Mui.Grid>
									</>
								)}

								{/* Lead Activity */}
								<Mui.Grid item><Mui.Divider /></Mui.Grid>
								<Mui.Grid item>
									<Mui.Typography className={classes.sectionHeader}>Lead Activity</Mui.Typography>
								</Mui.Grid>
								<Mui.Grid item>
									<Mui.Grid container spacing={2}>
										<Mui.Grid item xs={6}>
											<Mui.Typography className={classes.fieldLabel}>Status</Mui.Typography>
											<div>
												<Mui.Chip
													label={lead.status.label}
													className={lead.status.active ? classes.activeChip : classes.inactiveChip}
												/>
												{!lead.status.active && (
													<Mui.Button
														className={classes.reactivateButtonLabel}
														variant="contained"
														color={"primary"}
														size="small"
														onClick={() => this.reactivateLead()}
													>
														Reactivate
													</Mui.Button>
												)}
											</div>
										</Mui.Grid>
										{user.permissions.crm && env.leadRanking && (
											<Mui.Grid item xs={6}>
												<Mui.Typography className={classes.fieldLabel}>Engagement</Mui.Typography>
												<Ranking
													leadId={lead.id}
													sortedLeadsByRanking={leadRankData.sortedLeads}
													ratingSize={leadRankData.ratingSize}
												/>
											</Mui.Grid>
										)}
										{user.permissions.leadLastActive && lead.lastActiveOn && (
											<Mui.Grid item xs={6}>
												<Mui.Typography className={classes.fieldLabel}>Last Active</Mui.Typography>
												<Mui.Typography>{numberedDateFormatter(lead.lastActiveOn)}</Mui.Typography>
											</Mui.Grid>
										)}
									</Mui.Grid>
								</Mui.Grid>

							</Mui.Grid>
						</Mui.CardContent>
					</AppCard>
				</>
			);
	}
}

export const LeadDetails = withStyles(connect(mapStateToProps, mapDispatchToProps)(Component), styles);
